import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Mail, Trash2, RefreshCw, Eye, EyeOff, CheckCircle, AlertCircle, Inbox, Plus, Upload, Send, Package, ChevronLeft, ChevronRight, Search, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Card,
  CardContent,
} from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Expandable Email Card Component
function EmailCard({ email, formatDate }) {
  const [expanded, setExpanded] = useState(false);
  
  // Use full body if available, fallback to preview
  const fullContent = email.body || email.preview;
  
  return (
    <div 
      className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-4 cursor-pointer hover:bg-zinc-800/70 transition-colors"
      onClick={() => setExpanded(!expanded)}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h4 className="text-white font-medium truncate">{email.subject}</h4>
            {expanded ? (
              <ChevronUp className="w-4 h-4 text-zinc-400 flex-shrink-0" />
            ) : (
              <ChevronDown className="w-4 h-4 text-zinc-400 flex-shrink-0" />
            )}
          </div>
          <p className="text-sm text-zinc-400 mt-1">From: {email.sender_name || email.sender}</p>
          {expanded ? (
            <div className="mt-3 p-3 bg-zinc-900/50 rounded border border-zinc-700 max-h-[400px] overflow-y-auto">
              <p className="text-sm text-zinc-300 whitespace-pre-wrap break-words">{fullContent}</p>
            </div>
          ) : (
            <p className="text-sm text-zinc-500 mt-2 line-clamp-2">{email.preview}</p>
          )}
        </div>
        <div className="text-xs text-zinc-500 whitespace-nowrap">{formatDate(email.received_at)}</div>
      </div>
      {!expanded && (
        <p className="text-xs text-zinc-500 mt-2 italic">Click to view full content...</p>
      )}
    </div>
  );
}

export default function Email() {
  const [configs, setConfigs] = useState([]);
  const [isManager, setIsManager] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [fetchingEmails, setFetchingEmails] = useState(null);
  const [showEmailsDialog, setShowEmailsDialog] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showActivateDialog, setShowActivateDialog] = useState(false);
  const [emails, setEmails] = useState([]);
  const [currentConfigName, setCurrentConfigName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showRefreshToken, setShowRefreshToken] = useState(false);
  
  // Search state
  const [configSearch, setConfigSearch] = useState('');
  const [stockSearch, setStockSearch] = useState('');
  
  // Configs pagination
  const [configsPage, setConfigsPage] = useState(1);
  const [configsTotalPages, setConfigsTotalPages] = useState(1);
  const [configsTotal, setConfigsTotal] = useState(0);
  
  // Stock state
  const [stock, setStock] = useState([]);
  const [stockPage, setStockPage] = useState(1);
  const [stockTotalPages, setStockTotalPages] = useState(1);
  const [stockTotal, setStockTotal] = useState(0);
  const [uploadingStock, setUploadingStock] = useState(false);
  const fileInputRef = useRef(null);
  
  // VAs list for assignment
  const [vas, setVas] = useState([]);
  const [selectedVaId, setSelectedVaId] = useState('');
  const [selectedConfigId, setSelectedConfigId] = useState('');
  const [selectedStockId, setSelectedStockId] = useState('');
  const [activateName, setActivateName] = useState('');
  const [testingStock, setTestingStock] = useState(null);
  const [activating, setActivating] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    outlook_email: '',
    outlook_password: '',
    refresh_token: '',
    client_id: ''
  });

  useEffect(() => {
    fetchConfigs();
  }, [configsPage]);

  useEffect(() => {
    if (isManager) {
      fetchStock();
      fetchVas();
    }
  }, [isManager, stockPage, stockSearch]);

  // Debounced config search
  useEffect(() => {
    const timer = setTimeout(() => {
      setConfigsPage(1);
      fetchConfigs();
    }, 300);
    return () => clearTimeout(timer);
  }, [configSearch]);

  const fetchConfigs = async () => {
    try {
      const response = await axios.get(`${API_URL}/email/configs?search=${encodeURIComponent(configSearch)}&page=${configsPage}&limit=5`);
      setConfigs(response.data.configs || []);
      setIsManager(response.data.is_manager || false);
      setConfigsTotal(response.data.total || 0);
      setConfigsTotalPages(response.data.total_pages || 1);
    } catch (error) {
      console.error('Error fetching email configs:', error);
      toast.error('Failed to fetch email configurations');
    } finally {
      setLoading(false);
    }
  };

  const fetchStock = async () => {
    try {
      const response = await axios.get(`${API_URL}/email/stock?page=${stockPage}&limit=5&search=${encodeURIComponent(stockSearch)}`);
      setStock(response.data.items || []);
      setStockTotalPages(response.data.total_pages || 1);
      setStockTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching stock:', error);
    }
  };

  const fetchVas = async () => {
    try {
      const response = await axios.get(`${API_URL}/email/vas`);
      setVas(response.data.vas || []);
    } catch (error) {
      console.error('Error fetching VAs:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleTestConnection = async () => {
    if (!formData.refresh_token || !formData.client_id) {
      toast.error('Please fill in Refresh Token and Client ID');
      return;
    }

    setTesting(true);
    try {
      const response = await axios.post(`${API_URL}/email/test-connection`, formData);
      toast.success(response.data.message);
    } catch (error) {
      console.error('Connection test failed:', error);
      toast.error(error.response?.data?.detail || 'Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  const handleSaveConfig = async () => {
    if (!formData.name || !formData.outlook_email || !formData.refresh_token || !formData.client_id) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API_URL}/email/config`, formData);
      toast.success('Email configuration saved successfully');
      fetchConfigs();
      setShowAddDialog(false);
      setFormData({ name: '', outlook_email: '', outlook_password: '', refresh_token: '', client_id: '' });
    } catch (error) {
      console.error('Error saving config:', error);
      toast.error(error.response?.data?.detail || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfig = async (configId, configName) => {
    if (!window.confirm(`Delete "${configName}"? It will be moved back to stock.`)) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/email/config/${configId}`);
      toast.success('Email configuration deleted and moved to stock');
      fetchConfigs();
      fetchStock();
    } catch (error) {
      console.error('Error deleting config:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete configuration');
    }
  };

  const handleAssignConfig = async () => {
    if (!selectedVaId || !selectedConfigId) {
      toast.error('Please select a VA');
      return;
    }

    try {
      await axios.post(`${API_URL}/email/config/${selectedConfigId}/assign?va_id=${selectedVaId}`);
      toast.success('Email access assigned successfully');
      fetchConfigs();
      setShowAssignDialog(false);
      setSelectedVaId('');
      setSelectedConfigId('');
    } catch (error) {
      console.error('Error assigning config:', error);
      toast.error(error.response?.data?.detail || 'Failed to assign configuration');
    }
  };

  const handleFetchEmails = async (configId, configName) => {
    setFetchingEmails(configId);
    try {
      const response = await axios.get(`${API_URL}/email/instagram/${configId}`);
      setEmails(response.data.emails || []);
      setCurrentConfigName(configName);
      setShowEmailsDialog(true);
      toast.success(`Found ${response.data.count} Instagram emails`);
    } catch (error) {
      console.error('Error fetching emails:', error);
      toast.error(error.response?.data?.detail || 'Failed to fetch emails');
    } finally {
      setFetchingEmails(null);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.txt')) {
      toast.error('Only .txt files are allowed');
      return;
    }

    setUploadingStock(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API_URL}/email/stock/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(response.data.message);
      if (response.data.errors?.length > 0) {
        response.data.errors.forEach(err => toast.error(err));
      }
      fetchStock();
    } catch (error) {
      console.error('Error uploading stock:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload stock');
    } finally {
      setUploadingStock(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleTestStock = async (stockId) => {
    setTestingStock(stockId);
    try {
      const response = await axios.post(`${API_URL}/email/stock/${stockId}/test`);
      if (response.data.success) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Test failed');
    } finally {
      setTestingStock(null);
    }
  };

  const handleDeleteStock = async (stockId) => {
    if (!window.confirm('Permanently delete this stock item?')) return;

    try {
      await axios.delete(`${API_URL}/email/stock/${stockId}`);
      toast.success('Stock item deleted');
      fetchStock();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleActivateStock = async () => {
    if (!activateName || !selectedVaId || !selectedStockId) {
      toast.error('Please fill in all fields');
      return;
    }

    setActivating(true);
    try {
      const response = await axios.post(
        `${API_URL}/email/stock/${selectedStockId}/activate?name=${encodeURIComponent(activateName)}&va_id=${selectedVaId}`
      );
      toast.success(response.data.message);
      fetchConfigs();
      fetchStock();
      setShowActivateDialog(false);
      setActivateName('');
      setSelectedVaId('');
      setSelectedStockId('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Activation failed');
    } finally {
      setActivating(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const openAddDialog = () => {
    setFormData({ name: '', outlook_email: '', outlook_password: '', refresh_token: '', client_id: '' });
    setShowPassword(false);
    setShowRefreshToken(false);
    setShowAddDialog(true);
  };

  const openAssignDialog = (configId) => {
    setSelectedConfigId(configId);
    setSelectedVaId('');
    setShowAssignDialog(true);
  };

  const openActivateDialog = (stockId) => {
    setSelectedStockId(stockId);
    setActivateName('');
    setSelectedVaId('');
    setShowActivateDialog(true);
  };

  // Pagination helper
  const getPaginationNumbers = (currentPage, totalPages) => {
    const pages = [];
    const maxVisible = 5;
    
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      
      if (currentPage > 3) pages.push('...');
      
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      
      for (let i = start; i <= end; i++) pages.push(i);
      
      if (currentPage < totalPages - 2) pages.push('...');
      
      pages.push(totalPages);
    }
    
    return pages;
  };

  // Render pagination component
  const renderPagination = (currentPage, totalPages, setPage) => {
    if (totalPages <= 1) return null;
    
    return (
      <div className="flex items-center justify-center gap-2 mt-4">
        <Button
          onClick={() => setPage(p => Math.max(1, p - 1))}
          disabled={currentPage === 1}
          variant="outline"
          size="sm"
          className="border-zinc-700 text-white hover:bg-zinc-800"
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>
        
        {getPaginationNumbers(currentPage, totalPages).map((pageNum, idx) => (
          pageNum === '...' ? (
            <span key={`ellipsis-${idx}`} className="text-zinc-500 px-2">...</span>
          ) : (
            <Button
              key={pageNum}
              onClick={() => setPage(pageNum)}
              variant={currentPage === pageNum ? "default" : "outline"}
              size="sm"
              className={currentPage === pageNum 
                ? "bg-white text-black" 
                : "border-zinc-700 text-white hover:bg-zinc-800"
              }
            >
              {pageNum}
            </Button>
          )
        ))}
        
        <Button
          onClick={() => setPage(p => Math.min(totalPages, p + 1))}
          disabled={currentPage === totalPages}
          variant="outline"
          size="sm"
          className="border-zinc-700 text-white hover:bg-zinc-800"
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-8 h-8 animate-spin text-zinc-400" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="email-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-4xl md:text-5xl font-bold font-heading text-white tracking-tight" data-testid="email-title">
          Email
        </h1>
        {isManager && (
          <Button onClick={openAddDialog} className="bg-white text-black hover:bg-zinc-200" data-testid="add-email-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Email Access
          </Button>
        )}
      </div>

      {/* Active Configurations */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-white">
            {isManager ? `Active Configurations (${configsTotal})` : 'Your Email Access'}
          </h2>
          {isManager && (
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <Input
                placeholder="Search by name, email, VA..."
                value={configSearch}
                onChange={(e) => setConfigSearch(e.target.value)}
                className="bg-zinc-800 border-zinc-700 text-white pl-10"
              />
            </div>
          )}
        </div>
        
        {configs.length === 0 ? (
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="py-12 text-center">
              <Mail className="w-12 h-12 mx-auto mb-4 text-zinc-600" />
              <h3 className="text-lg font-medium text-white mb-2">
                {configSearch ? 'No results found' : (isManager ? 'No Email Configured' : 'No Email Access Assigned')}
              </h3>
              <p className="text-zinc-400 mb-6">
                {configSearch ? 'Try a different search term' : (isManager ? 'Add an email access or activate one from stock' : 'Contact your manager to get email access')}
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="grid gap-4">
              {configs.map((config) => (
                <Card key={config.config_id} className="bg-zinc-900/50 border-zinc-800">
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        </div>
                        <div>
                          <h3 className="text-white font-medium">{config.name}</h3>
                          <p className="text-sm text-zinc-400">{config.outlook_email}</p>
                          {isManager && config.assigned_to_name && (
                            <p className="text-xs text-blue-400">Assigned to: {config.assigned_to_name}</p>
                          )}
                          {config.last_used && (
                            <p className="text-xs text-zinc-500">Last used: {formatDate(config.last_used)}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleFetchEmails(config.config_id, config.name)}
                          disabled={fetchingEmails === config.config_id}
                          className="bg-white text-black hover:bg-zinc-200"
                          data-testid={`fetch-emails-btn-${config.config_id}`}
                        >
                          {fetchingEmails === config.config_id ? (
                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Inbox className="w-4 h-4 mr-2" />
                          )}
                          View Emails
                        </Button>
                        {isManager && !config.assigned_to && (
                          <Button
                            onClick={() => openAssignDialog(config.config_id)}
                            variant="outline"
                            className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                          >
                            <Send className="w-4 h-4 mr-2" />
                            Give to VA
                          </Button>
                        )}
                        {isManager && (
                          <Button
                            onClick={() => handleDeleteConfig(config.config_id, config.name)}
                            variant="outline"
                            size="icon"
                            className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                            data-testid={`delete-config-btn-${config.config_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            {renderPagination(configsPage, configsTotalPages, setConfigsPage)}
          </>
        )}
      </div>

      {/* Stock Section (Managers Only) */}
      {isManager && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-white flex items-center gap-2">
              <Package className="w-5 h-5" />
              Stock ({stockTotal})
            </h2>
            <div className="flex gap-3">
              <div className="relative w-56">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                <Input
                  placeholder="Search: new, re-use, email..."
                  value={stockSearch}
                  onChange={(e) => { setStockSearch(e.target.value); setStockPage(1); }}
                  className="bg-zinc-800 border-zinc-700 text-white pl-10"
                />
              </div>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                accept=".txt"
                className="hidden"
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingStock}
                variant="outline"
                className="border-zinc-700 text-white hover:bg-zinc-800"
              >
                {uploadingStock ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4 mr-2" />
                )}
                Upload Stock (.txt)
              </Button>
            </div>
          </div>

          {stock.length === 0 ? (
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardContent className="py-8 text-center">
                <Package className="w-10 h-10 mx-auto mb-3 text-zinc-600" />
                <p className="text-zinc-400">
                  {stockSearch ? 'No results found' : 'No stock items. Upload a .txt file to add email credentials.'}
                </p>
                {!stockSearch && <p className="text-xs text-zinc-500 mt-2">Format: email:password:refresh_token:client_id</p>}
              </CardContent>
            </Card>
          ) : (
            <>
              <div className="grid gap-3">
                {stock.map((item) => (
                  <Card key={item.stock_id} className="bg-zinc-800/50 border-zinc-700">
                    <CardContent className="py-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {/* Status badge */}
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            item.is_reuse 
                              ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30' 
                              : 'bg-green-500/20 text-green-400 border border-green-500/30'
                          }`}>
                            {item.is_reuse ? 're-use' : 'new'}
                          </span>
                          <div>
                            <p className="text-white font-mono text-sm">{item.outlook_email}</p>
                            <div className="flex items-center gap-3 text-xs text-zinc-500">
                              <span>Added: {formatDate(item.created_at)}</span>
                              {item.is_reuse && item.last_used && (
                                <span>Last used: {formatDate(item.last_used)}</span>
                              )}
                              {item.is_reuse && item.last_assigned_to && (
                                <span className="text-purple-400">Previously: {item.last_assigned_to}</span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            onClick={() => handleTestStock(item.stock_id)}
                            disabled={testingStock === item.stock_id}
                            variant="outline"
                            size="sm"
                            className="border-zinc-600 text-white hover:bg-zinc-700"
                            title="Test connection"
                          >
                            {testingStock === item.stock_id ? (
                              <RefreshCw className="w-3 h-3 animate-spin" />
                            ) : (
                              <AlertCircle className="w-3 h-3" />
                            )}
                          </Button>
                          <Button
                            onClick={() => openActivateDialog(item.stock_id)}
                            variant="outline"
                            size="sm"
                            className="border-green-500/50 text-green-400 hover:bg-green-500/10"
                          >
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Activate
                          </Button>
                          <Button
                            onClick={() => handleDeleteStock(item.stock_id)}
                            variant="outline"
                            size="icon"
                            className="border-red-500/50 text-red-400 hover:bg-red-500/10 h-8 w-8"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
              {renderPagination(stockPage, stockTotalPages, setStockPage)}
            </>
          )}
        </div>
      )}

      {/* Add Email Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white">Add Email Access</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Configure a new Outlook account for Instagram emails
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-white">Connection Name *</Label>
                <Input name="name" value={formData.name} onChange={handleInputChange}
                  placeholder="e.g., Model Account 1" className="bg-zinc-800 border-zinc-700 text-white" />
              </div>
              <div className="space-y-2">
                <Label className="text-white">Outlook Email *</Label>
                <Input name="outlook_email" type="email" value={formData.outlook_email} onChange={handleInputChange}
                  placeholder="email@outlook.com" className="bg-zinc-800 border-zinc-700 text-white" />
              </div>
              <div className="space-y-2">
                <Label className="text-white">Password</Label>
                <div className="relative">
                  <Input name="outlook_password" type={showPassword ? "text" : "password"}
                    value={formData.outlook_password} onChange={handleInputChange}
                    className="bg-zinc-800 border-zinc-700 text-white pr-10" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-white">Client ID *</Label>
                <Input name="client_id" value={formData.client_id} onChange={handleInputChange}
                  className="bg-zinc-800 border-zinc-700 text-white font-mono text-sm" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-white">Refresh Token *</Label>
              <div className="relative">
                <Input name="refresh_token" type={showRefreshToken ? "text" : "password"}
                  value={formData.refresh_token} onChange={handleInputChange}
                  className="bg-zinc-800 border-zinc-700 text-white font-mono text-sm pr-10" />
                <button type="button" onClick={() => setShowRefreshToken(!showRefreshToken)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white">
                  {showRefreshToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="flex gap-3 pt-4 justify-end">
              <Button onClick={() => setShowAddDialog(false)} variant="outline" className="border-zinc-700 text-white hover:bg-zinc-800">Cancel</Button>
              <Button onClick={handleTestConnection} disabled={testing || !formData.refresh_token || !formData.client_id}
                variant="outline" className="border-zinc-700 text-white hover:bg-zinc-800">
                {testing ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <AlertCircle className="w-4 h-4 mr-2" />}
                Test
              </Button>
              <Button onClick={handleSaveConfig} disabled={saving || !formData.name || !formData.outlook_email || !formData.refresh_token || !formData.client_id}
                className="bg-white text-black hover:bg-zinc-200">
                {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign to VA Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-white">Assign to VA</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Select a VA to give this email access to
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <Select value={selectedVaId} onValueChange={setSelectedVaId}>
              <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white data-[placeholder]:text-white [&>svg]:text-white">
                <SelectValue placeholder="Select a VA" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-800 border-zinc-700">
                {vas.map((va) => (
                  <SelectItem 
                    key={va.va_id} 
                    value={va.va_id} 
                    className="text-white hover:bg-zinc-700 focus:bg-zinc-700 focus:text-white cursor-pointer"
                  >
                    <span className="text-white">{va.name} ({va.role})</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex gap-3 justify-end">
              <Button onClick={() => setShowAssignDialog(false)} variant="outline" className="border-zinc-700 text-white hover:bg-zinc-800">Cancel</Button>
              <Button onClick={handleAssignConfig} disabled={!selectedVaId} className="bg-white text-black hover:bg-zinc-200">
                <Send className="w-4 h-4 mr-2" />
                Assign
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Activate Stock Dialog */}
      <Dialog open={showActivateDialog} onOpenChange={setShowActivateDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-white">Activate Email Access</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Give this email access a name and assign to a VA
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label className="text-white">Connection Name *</Label>
              <Input value={activateName} onChange={(e) => setActivateName(e.target.value)}
                placeholder="e.g., Model Account 1" className="bg-zinc-800 border-zinc-700 text-white" />
            </div>
            <div className="space-y-2">
              <Label className="text-white">Assign to VA *</Label>
              <Select value={selectedVaId} onValueChange={setSelectedVaId}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                  <SelectValue placeholder="Select a VA" className="text-white" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  {vas.map((va) => (
                    <SelectItem 
                      key={va.va_id} 
                      value={va.va_id} 
                      className="text-white hover:bg-zinc-700 focus:bg-zinc-700 focus:text-white cursor-pointer"
                    >
                      <span className="text-white">{va.name} ({va.role})</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-3 justify-end">
              <Button onClick={() => setShowActivateDialog(false)} variant="outline" className="border-zinc-700 text-white hover:bg-zinc-800">Cancel</Button>
              <Button onClick={handleActivateStock} disabled={activating || !activateName || !selectedVaId}
                className="bg-white text-black hover:bg-zinc-200">
                {activating ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                Activate & Assign
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Instagram Emails Dialog */}
      <Dialog open={showEmailsDialog} onOpenChange={setShowEmailsDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Instagram Emails - {currentConfigName} ({emails.length})
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto space-y-3 pr-2">
            {emails.length === 0 ? (
              <div className="text-center py-12 text-zinc-400">
                <Inbox className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No Instagram emails found</p>
              </div>
            ) : (
              emails.map((email, index) => (
                <EmailCard key={email.id || index} email={email} formatDate={formatDate} />
              ))
            )}
          </div>
          <div className="pt-4 border-t border-zinc-800 flex justify-end">
            <Button onClick={() => setShowEmailsDialog(false)} variant="outline" className="border-zinc-700 text-white hover:bg-zinc-800">Close</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
